// M4 world §17 — Era V transition card data and the 2000 / 2014 Quick Starts (rules.QUICKSTART_2000 / _2014).
// Built on the M3 builder (sim/quickstart.js quickStartM3: wipe, day/era/cash/rep, news & flags up to the day, techs
// leaked through the prior year, rigs + gear + crews, wells, platform, transport, labs). QS-2014 adds shale
// horizontals (lateral with bench & length at the bench's landing TVD, A's modified-hyperbolic production when the
// shale module is present), Midland DUCs, a 180-day frac-fleet contract, an SWD, crude-by-rail for the Bakken, 50%
// swaps at $95 for 12 months and a $250M RBL with $150M drawn (B's finance when present). Same seed → same game.
import { dateToDay, dayToDate, yearFrac } from '../../core/time.js';
import { ERA_BY_ID } from '../../data/eras.js';
import { NEWS } from '../../data/news.js';
import { QUICKSTART_2000, QUICKSTART_2014, shaleIp30, modHypRate, henryHub, wtiRatio, M4_FROM, LATERAL } from '../../data/m4/rules.js';
import { SANCTIONS } from '../../data/m4/regions.js';
import { WELL_CONTROL_REGIMES } from '../../data/m4/rules.js';
import { addItem, addStock } from '../../core/state.js';
import { EQUIPMENT, FIELDS, eqDef, fieldDef, fieldTransports, isAvailable } from '../defs.js';
import { reservoirsAt } from '../geo.js';
import { createWell } from '../drilling.js';
import { startProduction } from '../production.js';
import { leaseRoyalty } from '../survey.js';
import { quickStartM3 } from '../quickstart.js';
import { stampWell } from '../m3/offshore.js';
import { hedgeState } from '../m3/hedging.js';
import { nextId } from '../util.js';
import * as shaleProd from './shaleProd.js';         // A (optional): startShaleProduction
import * as shaleSvc from './shaleServices.js';      // A (optional): contractFracServiceImpl, buildWaterFacilityImpl
import * as mktFin from './marketFinance.js';        // B (optional): setupRbl, hedgeM4Impl
import { W } from './worldCore.js';
import { applyM4WorldNewsEffect } from './worldNews.js';

export { QUICKSTART_2000, QUICKSTART_2014 };
const ERA_FLAGS = (day) => ({ pioneer: dateToDay(1859, 8, 1), gusher: dateToDay(1901, 1, 1), postwar: dateToDay(1946, 1, 1), offshore: dateToDay(1973, 10, 16), shale: day });

// Plots on a field's prospects with a reservoir (optionally a given bench), not owned, centre-first, deterministic.
function freePlots(state, fid, need, bench = null) {
  const f = state.fields[fid];
  const fdef = fieldDef(fid);
  if (!f || !fdef) return [];
  return f.plots.map((p, i) => ({ i, p, res: reservoirsAt(fdef, p) }))
    .filter((x) => x.res.length && (!bench || x.res.some((r) => r.id === bench)) && !x.p.owned && !x.p.wellId)
    .sort((a, b) => (a.p.truth.edge ?? 1) - (b.p.truth.edge ?? 1) || a.i - b.i).slice(0, need);
}

function finishM4(state, Q, year) {
  state.flags.eraSummaryShown = ERA_FLAGS(Q.day);
  state.startYear = year;
  const Wm = W(state);
  // already-in-force regimes and sanctions are history, not news, on day one
  for (const g of WELL_CONTROL_REGIMES) if (g.from <= Q.day) Wm.regimesSeen[g.id] = g.from;
  for (const s of SANCTIONS) if (yearFrac(Q.day) >= s.from) Wm.regimesSeen[`sanction:${s.id}`] = Q.day;
  // M4 news effects up to today: records only (megaproject phases, regimes, sanctions) — no shut-ins or seizures
  for (const n of NEWS) if (n.day <= Q.day && n.day >= dateToDay(M4_FROM, 1, 1)) applyM4WorldNewsEffect(state, n, { replay: true });
  for (const l of Q.leases ?? []) for (const x of freePlots(state, l.fieldId, l.plots)) { x.p.owned = true; x.p.leaseDay = Q.day - 400; x.p.leaseExpires = null; x.p.royalty = leaseRoyalty(state, l.fieldId); }
}

// ---- 2000 "Y2K independent" ------------------------------------------------------------------------------------------
export function quickStart2000(state) {
  const Q = QUICKSTART_2000;
  quickStartM3(state, Q, 2000);
  finishM4(state, Q, 2000);
  // Q.logistics (supply boat, helicopter) are contracted when an offshore rig goes to work (quickStartM3: rig only)
  state.company.cash = Q.cash;
  return state;
}

// ---- 2014 "top of the cycle" ------------------------------------------------------------------------------------------
function shaleWell(state, rig, fid, idx, spec, duc) {
  const Q = QUICKSTART_2014;
  const day = Q.day;
  const fdef = fieldDef(fid);
  const p = state.fields[fid].plots[idx];
  const res = reservoirsAt(fdef, p);
  const r = res.find((x) => x.id === spec.bench) ?? res[0];
  const age = duc ? 0 : spec.ageDays ?? 365;
  p.owned = true; p.leaseDay = day - age - 300; p.leaseExpires = null; p.royalty = leaseRoyalty(state, fid);
  const tvd = Math.round(r.top + (r.thick ?? 100) / 2);
  const L = spec.lateralFt ?? 7500;
  const w = createWell(state, rig, fid, idx, tvd);
  w.rigId = null; w.bitUid = null; w.rigType = 'rotary';
  w.spudDay = day - age - (duc ? 90 : 60);
  w.depth = tvd; w.target = tvd; w.payTop = r.top; w.payFt = r.thick ?? 100; w.reservoirId = r.id; w.revealed = [r.id];
  w.strikeDay = w.spudDay + 20; w.strikeRigCond = 90;
  w.casing = [{ defId: eqDef('production_casing_p110') ? 'production_casing_p110' : 'modern_casing', top: 0, bottom: tvd, cemented: 'portland_cement', day: w.spudDay + 15 }];
  w.shoe = tvd;
  const spacing = 250;
  // same lateral record the shale sim writes (RSS build: radius 5730/bur ft, curve MD from LATERAL.curveMd)
  const bur = LATERAL.buildRateDegPer100?.rss ?? 10, radius = 5730 / bur, curveMd = Math.round(LATERAL.curveMd(bur));
  const kickoffMd = Math.max(0, Math.round(tvd - radius));
  w.lateral = { phase: 'done', bench: r.id, benchName: r.bench ?? r.name, lengthFt: L, targetFt: L, inZoneFt: Math.round(L * 0.88), inZoneFrac: 0.88, stages: Math.round(L / spacing),
    windowTop: r.top, windowFt: r.windowFt ?? 60, thick: r.thick, kickoffMd, curveMd, curveDrilled: curveMd, bur, tool: 'rss', landingTvd: tvd, landingTvdFt: tvd, verticalTarget: tvd,
    steer: 'auto', pos: 0, dipSign: 1, gamma: [], stuckEvents: 0, sidetracks: 0, parentOf: [], childOf: [],
    drilledDay: w.spudDay + 18, padUid: `${fid}:${idx}`, inherited: true };
  w.drilledBy = eqDef('ac_drive_land') ? 'ac_drive_land' : rig?.defId ?? null; // a 2013-era AC rig drilled these
  stampWell(state, w);
  w.md = kickoffMd + curveMd + L; // after stampWell, which sets md = water + TVD
  state.company.stats.wellsDrilled++;
  state.company.stats.ftDrilled += w.md;
  if (duc) { w.status = 'AT_TD'; w.duc = { since: day - 60 }; return w; }
  const tc = r.typeCurve ?? { ip30PerKft: [60, 100], de1: 0.66, b: 1.1, dmin: 0.08 };
  const ip30 = shaleIp30(tc, { lateralFt: L, proppantLbPerFt: 1400, stageSpacingFt: spacing, fluid: 'slickwater', inZoneFrac: 0.88, roll: p.truth.qiRoll ?? 0.5 });
  if (typeof shaleProd.startShaleProduction === 'function') shaleProd.startShaleProduction(state, w, ip30);
  else { w.status = 'AT_TD'; startProduction(state, w); w.prod.qi = Math.round(ip30 * 10) / 10; }
  w.status = 'PRODUCING';
  w.prod.age = age;
  w.prod.startDay = day - age;
  w.lateral.stagesCompleted = w.lateral.stages;
  w.firstProdYear = dayToDate(Math.max(0, day - age)).year;
  state.company.stats.strikes++;
  return w;
}
const oilRateOf = (w) => {
  const p = w.prod;
  if (!p) return 0;
  const q = p.shale ? modHypRate(p.qi, p.di, p.b, p.dmin, p.age / 365) : p.qi / Math.pow(1 + (p.b || 0.5) * (p.di || 0.6) * p.age / 365, 1 / (p.b || 0.5));
  return q * (p.streams?.oil ?? 1);
};

export function quickStart2014(state) {
  const Q = QUICKSTART_2014;
  quickStartM3(state, { ...Q, wells: [], platforms: [], transport: {}, licences: [] }, 2014);
  finishM4(state, Q, 2014);
  const day = Q.day;
  const rig = Object.values(state.rigs)[0];
  for (const r of Object.values(state.rigs)) { const spec = Q.rigs.find((s) => s.name === r.name); if (spec?.builtYear) r.builtYear = spec.builtYear; }
  const made = {};
  for (const ws of Q.wells) for (const x of freePlots(state, ws.fieldId, ws.count, ws.bench)) (made[ws.fieldId] ??= []).push(shaleWell(state, rig, ws.fieldId, x.i, ws, false));
  for (const d of Q.ducs ?? []) for (const x of freePlots(state, d.fieldId, d.count, d.bench)) (made[d.fieldId] ??= []).push(shaleWell(state, rig, d.fieldId, x.i, { ...d, lateralFt: 7500 }, true));
  // a 2014 shale operator already has its associated gas on a gathering system (ND/NM capture rules bite otherwise)
  for (const fid of Object.keys(made)) {
    const f = state.fields[fid], gas = fieldTransports(fieldDef(fid), 2014).map((t) => t.id ?? t).find((id) => eqDef(id)?.stats?.product === 'natural_gas' && isAvailable(eqDef(id), state));
    if (f && gas && !f.connected.includes(gas)) f.connected.push(gas);
  }
  // transport & a tank battery per field
  for (const [fid, tid] of Object.entries(Q.transport ?? {})) {
    const f = state.fields[fid];
    if (!f || !eqDef(tid)) continue;
    if (!f.connected.includes(tid)) f.connected.push(tid);
    f.transportId = tid;
  }
  for (const fid of Object.keys(made)) if (eqDef('steel_tank_55k')) addItem(state, 'steel_tank_55k', { type: 'field', id: fid });
  // services: frac fleet on a 180-day term, a salt-water disposal well
  for (const s of Q.services ?? []) {
    const def = eqDef(s.defId);
    if (!def) continue;
    const fid = s.fieldId ?? 'permian_midland';
    let r = null;
    if (def.cat === 'completion' && typeof shaleSvc.contractFracServiceImpl === 'function') r = shaleSvc.contractFracServiceImpl(state, s.defId, { fieldId: fid, days: s.termDays ?? 180 });
    else if (def.cat === 'water' && typeof shaleSvc.buildWaterFacilityImpl === 'function') r = shaleSvc.buildWaterFacilityImpl(state, s.defId, fid);
    if (!r?.ok) {
      const rec = (W(state).qsServices ??= []);
      rec.push({ defId: s.defId, fieldId: fid, startDay: day, endDay: s.termDays ? day + s.termDays : null });
      if (def.cat === 'water') addItem(state, s.defId, { type: 'field', id: fid });
    }
  }
  // hedges: 50% of PDP oil swapped at $95 for 12 months
  const pdpBblMonth = Object.values(state.wells).filter((w) => w.status === 'PRODUCING').reduce((a, w) => a + oilRateOf(w), 0) * 30.4;
  for (const h of Q.hedges ?? []) {
    const v = Math.round(pdpBblMonth * h.shareOfPdp);
    if (!(v > 0)) continue;
    let done = false;
    if (typeof mktFin.hedgeM4Impl === 'function') {
      const r = mktFin.hedgeM4Impl(state, { kind: h.kind, months: h.months, bblPerMonth: v, strike: h.price, skipCap: true });
      done = !!r?.ok;
    }
    if (!done) {
      const H = hedgeState(state);
      const legs = [];
      for (let i = 1; i <= h.months; i++) legs.push({ due: day + Math.round(i * 30.4), price: h.price, settled: false });
      H.positions.push({ id: `h${H.nextId++}`, kind: 'forward', side: 'short', bblPerMonth: v, bbl: v * h.months, openDay: day, expiryDay: legs[legs.length - 1].due, months: legs, pnl: 0, swap: true });
    }
  }
  // debt: the RBL
  const D = Q.debt;
  if (D) {
    let done = false;
    if (typeof mktFin.setupRbl === 'function') { try { mktFin.setupRbl(state, { commitment: D.rbl, drawn: D.drawn, ratePct: D.rate * 100 }); done = true; } catch { done = false; } }
    if (!done) state.company.loans.push({ id: nextId(state, 'L'), principal: D.drawn, ratePct: D.rate * 100, startDay: day, rbl: { commitment: D.rbl } });
  }
  state.company.cash = Q.cash;
  for (const w of Object.values(state.wells)) { w.inherited = true; if (w.prod && !w.prod.rate) w.prod.rate = Math.round(oilRateOf(w) / Math.max(0.05, w.prod.streams?.oil ?? 1) * 10) / 10; }
  return state;
}

// ---- views -----------------------------------------------------------------------------------------------------------
export function quickStartM4Options() {
  return [QUICKSTART_2000, QUICKSTART_2014].map((Q) => {
    const y = dayToDate(Q.day).year;
    return { year: y, day: Q.day, cash: Q.cash, reputation: Q.reputation, eraId: Q.eraId, note: Q.note, label: y === 2000 ? 'Shale & Deepwater · 2000' : 'Top of the Cycle · 2014',
      place: y === 2000 ? 'Houston, Texas' : 'Midland, Texas', rigs: Q.rigs.map((r) => eqDef(r.defId)?.name ?? r.defId), wells: Q.wells.reduce((a, w) => a + w.count, 0) };
  });
}

// Era V card (1 Jan 2000): what changed, what to try first.
export function eraM4View(state) {
  const e = ERA_BY_ID.shale;
  const y = Math.max(2000, yearFrac(state?.day ?? e.startDay));
  const y0 = 2000;
  const headline = NEWS.find((n) => n.id === 'm4_y2k')?.headline ?? 'Y2K Passes Quietly';
  const items = ['ac_drive_land', 'rotary_steerable', 'slickwater_frac', 'truss_spar', 'fpso_newbuild', 'mine_fleet', 'otsg_steam_plant', 'microseismic_monitoring']
    .map((id) => EQUIPMENT[id]).filter(Boolean).map((d) => ({ id: d.id, name: d.name, year: d.year }));
  const extra = Object.values(EQUIPMENT).filter((d) => d.year >= y0 && d.year <= y0 + 2 && !items.some((x) => x.id === d.id)).slice(0, Math.max(0, 8 - items.length)).map((d) => ({ id: d.id, name: d.name, year: d.year }));
  const fields = ['barnett', 'gulf_subsalt', 'angola_deepwater', 'nigeria_deepwater', 'orinoco_belt', 'kashagan', 'athabasca_mining', 'athabasca_sagd', 'north_sea_late_life']
    .filter((id) => FIELDS[id]).map((id) => ({ id, name: FIELDS[id].name, opensYear: dayToDate(FIELDS[id].opensDay ?? 0).year }));
  return {
    eraId: 'shale', roman: e.roman, name: e.name, startDay: e.startDay, blurb: e.blurb, title: 'Y2K passes quietly', headline,
    points: [
      { glyph: '⇶', head: 'Horizontal wells & multi-stage fracs', text: 'Mitchell’s slickwater fracs work in the Barnett. Rotary steerables and geosteering put two-mile laterals in a 50-ft window; the decline treadmill never stops.' },
      { glyph: '≋', head: 'Ultra-deepwater & pre-salt', text: 'Sixth- and seventh-generation drillships, subsalt and Lower Tertiary plays, Brazil’s pre-salt. Megaprojects take a decade and cost what they cost.' },
      { glyph: '⚠', head: 'Macondo changes the rules', text: 'After April 2010: a moratorium, slow permits, containment consortia, SEMS and the Well Control Rule. A deepwater blowout can cost more than the company.' },
      { glyph: '⚖', head: 'Sanctions, seizures & storms', text: 'Venezuela’s mixed companies, Kashagan, Russia after 2014 and 2022, Argentina’s capital controls; Ivan, Katrina, Rita, Ike, Harvey and Ida.' },
      { glyph: '◒', head: 'Gas is a product now', text: `Henry Hub ~$${henryHub(y).toFixed(2)}/MMBtu; hubs and takeaway decide what your barrels fetch (WTI ≈ ${Math.round(wtiRatio(y) * 100)}% of Brent).` },
    ],
    newItems: [...items, ...extra], newFields: fields, henryHub: Math.round(henryHub(y) * 100) / 100,
    shown: state?.flags?.eraSummaryShown?.shale ?? null,
  };
}
